import { Injectable } from '@angular/core';
import { ExportCompanyAllCrmParamsInterface } from '../models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { FeatureFlagsEnum } from '../enums';
import { LEGACY_THEME_CLASS } from '../constants';
import { take } from 'rxjs/operators';
import { OauthProvider } from '../../oauth/providers';
import {
  AccountExportAppWizardComponent,
} from '../components';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../core/services';
import { OauthService } from './oauth.service';
import { SalesforceService } from './crm/salesforce.service';
import { FeatureFlagService } from './feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class ExportCompanyComponentFactoryService {

  private readonly allowedTypes: string[] = [
    'companySalesforce',
    'Hubspot',
    'Dynamics',
    'Zoho',
    'custom',
  ];

  constructor(
    private toastr: ToastrService,
    private auth: AuthenticationService,
    private oauthService: OauthService,
    private salesforceService: SalesforceService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly matDialog: MatDialog,
  ) {
  }

  isAllowExport(type: string): boolean {
    return this.allowedTypes.includes(type);
  }

  exportOauthCRM(params: ExportCompanyAllCrmParamsInterface, churnZero: any): void {
    const modalConfig: MatDialogConfig = this.toMatDialogConfig(params);
    switch (params.type) {
      case 'companySalesforce':
        this.exportCompanySalesforce(modalConfig, params);
        churnZero.trackEventChurnZero('Portal - Company - Export Salesforce');
        break;
      case 'Hubspot':
        this.exportHubSpot(modalConfig, params);
        churnZero.trackEventChurnZero('Portal - Company - Export Hubspot');
        break;
      case 'Dynamics':
        this.exportDynamics(modalConfig, params);
        churnZero.trackEventChurnZero('Portal - Company - Export Dynamics');
        break;
      case 'Zoho':
        this.exportZoho(modalConfig, params);
        churnZero.trackEventChurnZero('Portal - Company - Export Zoho');
        break;
      case 'custom':
        this.exportCustom(modalConfig);
        break;
    }
  }

  private toMatDialogConfig(params: ExportCompanyAllCrmParamsInterface): MatDialogConfig {
    return {
      panelClass: ['modal-lg', LEGACY_THEME_CLASS],
      data: {
        company: params.selectedCompany,
        verified: params.isHumanVerified,
        international: params.isInternational,
        fromExport: params.fromExport,
        app: params.app,
        icpIntelJobUUID: params.icpIntelJobUUID || null,
      },
    };
  }

  private exportCompanySalesforce(modalConfig: MatDialogConfig, params: ExportCompanyAllCrmParamsInterface): void {
    if (this.auth.hasEmail('sfEmail') || params.fromExport === 'fromInboxIntelCompany') {
      this.featureFlagService.getFlag(FeatureFlagsEnum.WebsocketsSf).pipe(take(1)).subscribe((isWebsocketSfEnabled: boolean) => {
        if (isWebsocketSfEnabled && this.auth.getImpersonatedEmail()) {
          this.toastr.warning('Export is not supported while impersonating');
          return;
        }

        this.matDialog.open(params.component, modalConfig);
      });
    } else {
      this.salesforceService.isFromInboxIntelCompany =
        this.auth.hasEmail('sfEmail') && params.fromExport === 'fromInboxIntelCompany';

      this.openOauthCRM(
        OauthProvider.SalesforceCompany,
        params.selectedCompany,
        params.fromExport,
      );
    }
  }

  private exportHubSpot(modalConfig: MatDialogConfig, params: ExportCompanyAllCrmParamsInterface): void {
    if (this.auth.hasEmail('hsEmail')) {
      this.matDialog.open(params.component, modalConfig);
    } else {
      this.openOauthCRM(
        OauthProvider.Hubspot,
        params.selectedCompany,
        params.fromExport,
      );
    }
  }

  private exportDynamics(modalConfig: MatDialogConfig, params: ExportCompanyAllCrmParamsInterface): void {
    if (this.auth.hasEmail('dyEmail')) {
      this.matDialog.open(params.component, modalConfig);
    } else {
      this.openOauthCRM(
        OauthProvider.DynamicsCompany,
        params.selectedCompany,
        params.fromExport,
      );
    }
  }

  private exportZoho(modalConfig: MatDialogConfig, params: ExportCompanyAllCrmParamsInterface): void {
    if (this.auth.hasEmail('zhEmail')) {
      this.matDialog.open(params.component, modalConfig);
    } else {
      this.openOauthCRM(
        OauthProvider.ZohoCompany,
        params.selectedCompany,
        params.fromExport,
      );
    }
  }

  private exportCustom(modalConfig: MatDialogConfig): void {
    this.matDialog.open(
      AccountExportAppWizardComponent,
      modalConfig,
    );
  }

  private openOauthCRM(provider: OauthProvider, matchId: any[], isFromList?: string): void {
    this.oauthService.open(provider, matchId, isFromList);
  }
}
